/**
 * @module lib/oauthResume
 *
 * The one silent-refresh attempt the login page makes for an OAuth handshake.
 *
 * The backend's login handler bounces a browser whose access token expired to
 * `#/login?returnTo=<issuer>/oauth/login?login_challenge=…&resume=1`. The page
 * renews the session and goes back. A `sessionStorage` key per challenge makes
 * the attempt one-shot on this side too: arriving again for the same challenge
 * goes straight back with `resume_failed=1`, which the handler answers as
 * signed out.
 */

import { safeOAuthReturnTo } from './oauthReturnTo.js';

const KEY_PREFIX = 'oauth-resume:';

/**
 * @param {string} search Raw pre-hash query string (`window.location.search`)
 * @param {string} apiBase Absolute base of the API host (`API_ROOT`)
 * @param {Storage} storage Where the one-shot key lives (`window.sessionStorage`)
 * @returns {{url: string, failedUrl: string, alreadyTried: boolean}|null}
 *   Null when there is no safe login `returnTo` to resume
 */
export function oauthResumeTarget(search, apiBase, storage) {
  const url = safeOAuthReturnTo(search, apiBase, ['/oauth/login']);
  if (!url) return null;
  const challenge = new URL(url).searchParams.get('login_challenge');
  if (!challenge) return null;

  const failed = new URL(url);
  failed.searchParams.set('resume_failed', '1');

  const key = `${KEY_PREFIX}${challenge}`;
  let alreadyTried = true;
  try {
    alreadyTried = storage.getItem(key) !== null;
    if (!alreadyTried) storage.setItem(key, String(Date.now()));
  } catch {
    // Without storage the attempt cannot be counted: do not attempt it.
  }
  return { url, failedUrl: failed.href, alreadyTried };
}
